import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Boxes, CheckCircle2, AlertTriangle, XCircle, DollarSign, Mail, PlusCircle } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!stats) return <div className="text-slate-500">Loading dashboard…</div>;

  const cards = [
    { icon: Boxes, label: "Total products", value: stats.total_products, color: "bg-slate-900 text-white" },
    { icon: CheckCircle2, label: "Available", value: stats.available, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { icon: AlertTriangle, label: "Low stock", value: stats.low_stock, color: "bg-amber-50 text-amber-700 border-amber-200" },
    { icon: XCircle, label: "Out of stock", value: stats.out_of_stock, color: "bg-rose-50 text-rose-700 border-rose-200" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Overview</div>
          <h1 className="mt-1 font-display text-3xl font-bold">Dashboard</h1>
        </div>
        <Link to="/admin/products/new" data-testid="admin-add-product-cta" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white font-medium text-sm hover:bg-blue-600">
          <PlusCircle className="w-4 h-4" /> Add product
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
        {cards.map((c, i) => (
          <div key={i} className={`rounded-2xl p-5 border ${c.color}`}>
            <c.icon className="w-5 h-5 opacity-80" />
            <div className="mt-4 font-display text-3xl font-bold">{c.value}</div>
            <div className="text-xs mt-1 opacity-80">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl p-5 border border-slate-200 bg-white flex items-start gap-3">
          <span className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center"><DollarSign className="w-5 h-5 text-blue-600" /></span>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Inventory value</div>
            <div className="mt-1 font-display text-2xl font-bold">${stats.inventory_value.toLocaleString()}</div>
          </div>
        </div>
        <div className="rounded-2xl p-5 border border-slate-200 bg-white flex items-start gap-3">
          <span className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center"><Mail className="w-5 h-5 text-emerald-600" /></span>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">New inquiries</div>
            <div className="mt-1 font-display text-2xl font-bold">{stats.new_inquiries}</div>
            <Link to="/admin/inquiries" className="text-xs font-medium text-blue-600">Review →</Link>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Recently added">
          {stats.recent_products.length === 0 ? (
            <Empty msg="No products yet." />
          ) : (
            <ul className="divide-y divide-slate-200">
              {stats.recent_products.map(p => (
                <li key={p.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{p.sku} · {p.brand}</div>
                  </div>
                  <Link to={`/admin/products/${p.id}`} className="text-xs font-medium text-blue-600 whitespace-nowrap">Edit →</Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Low stock alert">
          {stats.low_stock_products.length === 0 ? (
            <Empty msg="Nothing running low. Nice." />
          ) : (
            <ul className="divide-y divide-slate-200">
              {stats.low_stock_products.map(p => (
                <li key={p.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-amber-700">{p.stock_quantity} left</div>
                  </div>
                  <Link to={`/admin/products/${p.id}`} className="text-xs font-medium text-blue-600">Restock →</Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}
function Empty({ msg }) { return <div className="py-8 text-center text-sm text-slate-500">{msg}</div>; }
